import { CloseIcon, DeleteIcon, EditIcon } from '@assets/icons';
import { CardButton, IconButton } from '@components/button';
import { Toggle } from '@components/form';
import { Group } from '@components/layout';
import { CreateMcpServerConfigModal, EditMcpServerConfigModal } from '@components/mcpServer';
import { useConfirmation, useModal } from '@components/modal';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import {
  useDeleteMcpServerMutation,
  useToggleWorkspaceMcpServerOverrideMutation,
  type McpServer,
} from '@generated/graphql';
import { Box, type SxProps } from '@mui/material';
import posthog from 'posthog-js';
import { ServerTag } from '../../ServerTag';
import { ResourceCard } from '../ResourceCard';

type McpServerCardProps = {
  server: Pick<
    McpServer,
    'name' | 'mcpServerId' | 'enabled' | 'description' | 'config' | 'scopeType'
  >;
  workspaceId?: string;
  removable?: boolean;
  editable?: boolean;
  auto?: boolean;
  toggleable?: boolean;
  enabled?: boolean;
  hideTag?: boolean;
  source: 'workspace' | 'organization' | 'user';
  onChange?: VoidFunction;
};

export const McpServerCard = ({
  server,
  workspaceId,
  removable = false,
  editable = false,
  toggleable = false,
  hideTag = false,
  enabled = false,
  auto = false,
  source,
  onChange,
}: McpServerCardProps) => {
  const { showConfirmation } = useConfirmation();

  const [toggleOverride] = useToggleWorkspaceMcpServerOverrideMutation();

  const { showModal: showEditMcpServerModal } = useModal({
    component: EditMcpServerConfigModal,
    componentProps: { server, workspaceId, onEdit: onChange },
    modalProps: {
      title: `Edit MCP server: ${server.name}`,
    },
  });

  const handleToggleMcpServer = () =>
    showConfirmation({
      onAffirm: async () => {
        await toggleOverride({
          variables: {
            workspaceId,
            mcpServerId: server.mcpServerId,
          },
          onCompleted: (data) => {
            const disabled = data?.toggleWorkspaceMcpServerOverride;
            posthog.capture(
              `workspace:user_${disabled ? 'disable' : 'enable'}-inherited-workspace-mcp-server`,
              {
                workspaceId,
                mcpServerId: server.mcpServerId,
              },
            );
            onChange?.();
          },
        });
      },
      message: `Are you sure you want to ${enabled ? 'disable' : 'enable'} ${server.name ? `"${server.name}"` : 'this MCP server'} for this workspace?`,
    });

  const handleEditMcpServer = () => {
    return showEditMcpServerModal();
  };

  const handleRemoveMcpServer = () =>
    showConfirmation({
      onAffirm: async () => {
        await toggleOverride({
          variables: {
            workspaceId,
            mcpServerId: server.mcpServerId,
          },
          onCompleted: () => {
            posthog.capture('workspace:user_remove-imported-workspace-mcp-server', {
              workspaceId,
              mcpServerId: server.mcpServerId,
            });
            onChange?.();
          },
        });
      },
      message: `Are you sure you want to remove ${server.name ? `"${server.name}"` : 'this MCP server'} from this workspace?`,
    });

  const [deleteMcpServerMutation] = useDeleteMcpServerMutation();
  const handleDeleteMcpServer = () => {
    showConfirmation({
      onAffirm: async () => {
        await deleteMcpServerMutation({
          variables: { mcpServerId: server.mcpServerId },
          update(cache) {
            cache.evict({ id: cache.identify(server) });
            cache.gc();
          },
          onCompleted: () => {
            posthog.capture('workspace:user_delete-workspace-mcp-server', {
              workspaceId,
              mcpServerId: server.mcpServerId,
            });
            onChange?.();
          },
        });
      },
      message: `Are you sure you want to delete ${server.name ? `"${server.name}"` : 'this MCP server'}?`,
    });
  };

  return (
    <ResourceCard>
      <Group alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="body1" handleOverFlow fitContent color="foreground.paperCard.default">
          {server.name}
        </Typography>
        {!hideTag && (
          <ServerTag
            label={source}
            color={source}
            variant="caption2"
            sx={{ textTransform: 'capitalize' }}
          />
        )}
        {auto && <ServerTag label="Auto" variant="caption2" />}
      </Group>
      <Group alignItems="center" justifyContent="flex-end" flexShrink={0} gap={1}>
        {toggleable && (
          <Tooltip title={enabled ? 'Disable MCP server' : 'Enable MCP server'}>
            <Box>
              <Toggle
                checked={enabled}
                onChange={handleToggleMcpServer}
                sx={{ '&.MuiFormControlLabel-root': { height: 'fit-content !important' } }}
              />
            </Box>
          </Tooltip>
        )}
        {editable && (
          <>
            <IconButton
              Icon={EditIcon}
              tooltip="Details"
              onClick={handleEditMcpServer}
              size={16}
              variant="default"
            />
            <IconButton
              Icon={DeleteIcon}
              tooltip="Delete"
              onClick={handleDeleteMcpServer}
              size={16}
              variant="error"
            />
          </>
        )}
        {removable && (
          <IconButton
            Icon={CloseIcon}
            tooltip="Remove"
            onClick={handleRemoveMcpServer}
            size={16}
            variant="error"
          />
        )}
      </Group>
    </ResourceCard>
  );
};

export const AddMcpServerCard = ({
  workspaceId,
  sx,
  onCreate,
}: {
  workspaceId: string;
  sx?: SxProps;
  onCreate?: VoidFunction;
}) => {
  const { showModal } = useModal({
    component: CreateMcpServerConfigModal,
    componentProps: {
      fixedScopeType: 'workspace',
      fixedWorkspaceId: workspaceId,
      onCreate,
    },
    modalProps: {
      title: 'Create workspace MCP server',
    },
  });

  const handleManageMcpServers = () => {
    void showModal();
  };

  return (
    <CardButton
      label="Add workspace MCP server"
      onClick={handleManageMcpServers}
      background="paperCard"
      sx={{ ...sx }}
    />
  );
};

export const ImportMcpServerCard = ({
  hasMcpServers,
  onClick,
  sx,
}: {
  hasMcpServers: boolean;
  onClick: () => void;
  sx?: SxProps;
}) => {
  return (
    <CardButton
      label={hasMcpServers ? 'Manage imported MCP servers' : 'Import MCP servers'}
      onClick={onClick}
      background="paperCard"
      sx={{ ...sx }}
    />
  );
};
